import axios from "axios";
import { useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { useDataContext } from "../../../utils/DataContext";
import { useNavigate } from "react-router-dom";
import { buttonText } from "../../../utils/buttonText";
import { pageText } from "../../../utils/pageText";

function MembersTable(props) {
  const { members, organizationName, fetchUsersData, priority, fetchPriorities } = props;

  const [inviteeEmail, setInviteeEmail] = useState(null);
  const [inviteeRole, setInviteeRole] = useState("regular");
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [newPriority, setNewPriority] = useState(priority || 5);

  const { userToken, userData } = useDataContext();

  const navigate = useNavigate();

  const inviteMember = async () => {
    if (!inviteeEmail) {
      window.alert("Please input an email.");
      return;
    }
    const url = "/auth/organization/invite";
    axios
      .post(url, {
        requesterToken: userToken,
        organization: organizationName,
        email: inviteeEmail,
        role: inviteeRole,
      })
      .then((response) => {
        fetchUsersData();
        setShowInvitationModal(false);
        setInviteeEmail(null);
        setInviteeRole("regular");
        window.alert(
          `Invite '${inviteeEmail}' to '${organizationName}' success.`
        );
      })
      .catch((error) => {
        const errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const removeMumber = async (email) => {
    const url = "/auth/organization/member/remove";
    axios
      .post(url, {
        requesterToken: userToken,
        email: email,
      })
      .then((response) => {
        fetchUsersData();
        window.alert(`Remove '${email}' from '${organizationName}' success.`);
      })
      .catch((error) => {
        const errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const addOrganizer = async (email) => {
    const url = "/auth/organization/organizer/add";
    axios
      .post(url, {
        requesterToken: userToken,
        email: email,
      })
      .then((response) => {
        fetchUsersData();
        window.alert(
          `Add '${email}' as organizer in '${organizationName}' success.`
        );
      })
      .catch((error) => {
        const errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const deleteOrganizer = async (email) => {
    const url = "/auth/organization/organizer/delete";
    axios
      .post(url, {
        requesterToken: userToken,
        email: email,
      })
      .then((response) => {
        fetchUsersData();
        window.alert(
          `Delete '${email}' as organizer from '${organizationName}' success.`
        );
      })
      .catch((error) => {
        const errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const quitOrganization = () => {
    removeMumber(userData.email);
    navigate("/signin");
  };

  const setPriorityHandler = async () => {
    if (!newPriority || newPriority < 1 || newPriority > 10) {
      window.alert("Priority must be between 1 and 10.");
      return;
    }
    const url = "/auth/organization/priority/set";
    axios
      .post(url, {
        requesterToken: userToken,
        organization: organizationName,
        priority: newPriority,
      })
      .then((response) => {
        if (fetchPriorities) {
          fetchPriorities();
        }
        setShowPriorityModal(false);
        window.alert(
          `Set priority for '${organizationName}' to ${newPriority} success.`
        );
      })
      .catch((error) => {
        const errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const hasPermission =
    userData &&
    (userData.role === "admin" || userData.organization_role === "organizer");

  const isAdmin = userData && userData.role === "admin";

  const memberEntries = members ? Object.entries(members) : [];

  const MembersTableComponent = (
    <div className="org-member-list">
      <div className="org-member-head">
        <span>{pageText("Member")}</span>
        <span>{pageText("Role")}</span>
      </div>
      {memberEntries.map(([email, role]) => {
        const isOrganizer = role === "organizer";
        let organizerButton;
        if (isAdmin) {
          organizerButton = isOrganizer ? (
            <button
              type="button"
              className="org-link-btn danger"
              onClick={() => deleteOrganizer(email)}
            >
              {buttonText("Remove organizer")}
            </button>
          ) : (
            <button
              type="button"
              className="org-link-btn"
              onClick={() => addOrganizer(email)}
            >
              {buttonText("Make organizer")}
            </button>
          );
        }
        return (
          <div className="org-member-row" key={email}>
            <div className="org-member-identity">
              <span className="org-member-avatar">
                {email.charAt(0).toUpperCase()}
              </span>
              <span className="org-member-email">{email}</span>
            </div>
            <div className="org-member-role">
              <span
                className={`org-role-badge ${
                  isOrganizer ? "organizer" : "regular"
                }`}
              >
                {pageText(isOrganizer ? "Organizer" : "Regular")}
              </span>
              {hasPermission && (
                <div className="org-member-actions">
                  {organizerButton}
                  <button
                    type="button"
                    className="org-icon-btn"
                    aria-label={buttonText(`Remove ${email}`)}
                    onClick={() => removeMumber(email)}
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const InvitationModal = (
    <Modal
      show={showInvitationModal}
      onHide={() => {
        setShowInvitationModal(false);
      }}
      centered
      dialogClassName="org-invite-modal"
    >
      <div className="org-invite-card">
        <div className="org-invite-header">
          <div className="org-invite-heading">
            <span className="org-invite-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm-8 1a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 2c-2.7 0-6 1.34-6 4v2h7v-2c0-1.1.45-2.07 1.2-2.85A9.6 9.6 0 0 0 8 14Zm8 0c-2.7 0-8 1.34-8 4v2h16v-2c0-2.66-5.3-4-8-4Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <h3 className="org-invite-title">
              {pageText("Invite new member to")}{" "}
              <code className="org-invite-org">{organizationName}</code>
            </h3>
          </div>
          <button
            type="button"
            className="org-invite-close"
            aria-label={buttonText("Close")}
            onClick={() => setShowInvitationModal(false)}
          >
            &times;
          </button>
        </div>
        <div className="org-invite-body">
          <div className="org-field">
            <label className="org-field-label">{pageText("Invitee email")}</label>
            <input
              className="org-field-input"
              type="email"
              placeholder="name@company.com"
              value={inviteeEmail || ""}
              onChange={(e) => {
                setInviteeEmail(e.target.value);
              }}
            />
            <span className="org-invite-hint">
              {pageText("They'll receive an email invite to join this workspace")}
            </span>
          </div>
          <div className="org-field">
            <label className="org-field-label">{pageText("Role")}</label>
            <select
              className="org-field-input"
              value={inviteeRole}
              onChange={(e) => {
                setInviteeRole(e.target.value);
              }}
            >
              <option value="regular">{pageText("Regular")}</option>
              {isAdmin && <option value="organizer">{pageText("Organizer")}</option>}
            </select>
          </div>
        </div>
        <div className="org-invite-footer">
          <button
            type="button"
            className="org-btn org-btn-secondary"
            onClick={() => setShowInvitationModal(false)}
          >
            {buttonText("Cancel")}
          </button>
          <button
            type="button"
            className="org-btn org-btn-primary org-invite-submit"
            onClick={inviteMember}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="m22 2-7 20-4-9-9-4Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
            {buttonText("Send invite")}
          </button>
        </div>
      </div>
    </Modal>
  );

  const PriorityModal = (
    <Modal
      show={showPriorityModal}
      onHide={() => {
        setShowPriorityModal(false);
      }}
    >
      <Modal.Header closeButton closeLabel={buttonText("Close")}>
        <Modal.Title>{pageText(`Set Priority for ${organizationName}`)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>
            {pageText("Priority (1-10, higher number = higher priority)")}
          </Form.Label>
          <Form.Control
            type="number"
            min="1"
            max="10"
            value={newPriority}
            onChange={(e) => {
              setNewPriority(parseInt(e.target.value));
            }}
          />
          <Form.Text className="text-muted">
            {pageText(`Current priority: ${priority || 5}`)}. {pageText("Higher numbers have higher priority for training pipeline access.")}
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowPriorityModal(false)}>
          {buttonText("Cancel")}
        </Button>
        <Button variant="primary" onClick={setPriorityHandler}>
          {buttonText("Set Priority")}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="org-card">
      <div className="org-card-header">
        <div>
          <h2 className="org-card-title">{pageText("Members")}</h2>
          <p className="org-card-subtitle">
            {pageText("People with access to")}{" "}
            <span className="organization-settings-name">
              {organizationName}
            </span>
            {priority && ` · ${pageText(`Priority ${priority}`)}`}
          </p>
        </div>
        <span className="org-count-badge">
          {pageText(`${memberEntries.length} member${memberEntries.length === 1 ? "" : "s"}`)}
        </span>
      </div>

      <div className="org-card-body">
        <div className="org-members-toolbar">
          <div className="org-members-toolbar-left">
            {hasPermission && (
              <button
                type="button"
                className="org-btn org-btn-primary"
                onClick={() => setShowInvitationModal(true)}
              >
                {buttonText("+ Invite new member")}
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                className="org-btn org-btn-secondary"
                onClick={() => {
                  setNewPriority(priority || 5);
                  setShowPriorityModal(true);
                }}
              >
                {buttonText("Set priority")}
              </button>
            )}
          </div>
          {userData.role !== "admin" && (
            <button
              type="button"
              className="org-btn org-btn-danger"
              onClick={quitOrganization}
            >
              {buttonText("Quit organization")}
            </button>
          )}
        </div>
        {MembersTableComponent}
      </div>

      <div className="org-card-footer">
        <span className="org-footer-note">
          <span className="org-dot-icon" />
          {pageText("Removing a member revokes their access immediately")}
        </span>
      </div>

      {InvitationModal}
      {PriorityModal}
    </div>
  );
}

export default MembersTable;
